import {useMemo, useState} from 'react';
import {useFieldArray, useFormContext} from 'react-hook-form';
import AppConfirmationDialog from '../../../../../../../components/common/AppConfirmationDialog';
import AppMenu from '../../../../../../../components/common/AppMenu';
import {SVG} from '../../../../../../../constants/assetLists';
import {useSnackbar} from '../../../../../../../context/SnackbarContextProvider';
import {useOnBoardingContext} from '../../../../../../../context/onBoarding/OnBoardingContextProvider';
import {NetworkDetailsFormValues, NetworkNodeFieldPath} from '../../../types';
import {
  getManualDepartments,
  getTemplateDepartments,
  getTemplateOrgDepartments,
} from './defaultDepartment';
import {
  cloneDepartmentsFromSource,
  collectDepartmentSourceOptions,
} from './departmentCopyUtils';
import {canReplaceDepartments} from './departmentReferenceUtils';
import Switcher from '../../Switcher';
import {getActiveSectionText} from '../../../../../../../utils';
import AppButton from '../../../../../../../components/common/AppButton';
import OnboardingValidationBadge from '../../OnboardingValidationBadge';
import { get } from 'react-hook-form';
type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
};

function DepartmentHeader({selectedFieldPath}: Props) {
  const [pendingDepartment, setPendingDepartment] = useState<string | null>(
    null,
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedSourceName, setSelectedSourceName] = useState<string | null>(
    null,
  );
  const [pendingSourcePath, setPendingSourcePath] =
    useState<NetworkNodeFieldPath | null>(null);
  const [showCopyConfirmation, setShowCopyConfirmation] = useState(false);

  const {control, setValue, watch, getValues,formState: { errors },} =
    useFormContext<NetworkDetailsFormValues>();
const departments = watch(`${selectedFieldPath}.departments`) ?? [];
const departmentError = get(
  errors,
  `${selectedFieldPath}.departments`
);
const showDepartmentValidation =
  departments.length === 0 ||
  !!departmentError;
  const {replace} = useFieldArray({
    control,
    name: `${selectedFieldPath}.departments`,
    keyName: 'id',
  });

  const {getDefaultSelectedNode, setDefaultSelectedNodeForPath} =
    useOnBoardingContext();
  const defaultSelectedNode = getDefaultSelectedNode(selectedFieldPath);
  const {showSnackbar} = useSnackbar();

  const network = watch('network') ?? [];

  const blockReplaceIfDepartmentsInUse = (): boolean => {
    const result = canReplaceDepartments(network, selectedFieldPath);
    if (!result.allowed) {
      showSnackbar({
        message: result.message,
        type: 'error',
        duration: 5000,
      });
      return true;
    }
    return false;
  };

  const menuItems = useMemo(
    () =>
      collectDepartmentSourceOptions(network, selectedFieldPath).map(o => ({
        title: o.title,
        value: o.value,
      })),
    [network, selectedFieldPath],
  );

  const buttonLabel = selectedSourceName
    ? `ייבוא הגדרות מ${selectedSourceName}`
    : 'ייבוא הגדרות מ...';

  const getDepartmentsForMode = (mode: string) => {
    const isOrgMode =
      getActiveSectionText(selectedFieldPath.toString()) === 'רשת';

    if (mode === 'manual') {
      return getManualDepartments();
    } else {
      return isOrgMode ? getTemplateOrgDepartments() : getTemplateDepartments();
    }
  };

  const handleSwitcherChange = (id: string) => {
    if (id === defaultSelectedNode.department) return;
    if (blockReplaceIfDepartmentsInUse()) return;

    setPendingDepartment(id);
    setShowConfirmation(true);
  };

  const handleConfirmChange = () => {
    if (!pendingDepartment) return;
    if (blockReplaceIfDepartmentsInUse()) {
      handleCloseConfirmation();
      return;
    }

    const newDepts = getDepartmentsForMode(pendingDepartment);
    replace(newDepts);
    setValue(`${selectedFieldPath}.departments`, newDepts, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue(`${selectedFieldPath}.roles`, []);
    setDefaultSelectedNodeForPath(selectedFieldPath, prev => ({
      ...prev,
      department: pendingDepartment,
    }));
    setShowConfirmation(false);
    setPendingDepartment(null);
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setPendingDepartment(null);
  };

  const handleSourceSelect = (sourcePath: string) => {
    if (blockReplaceIfDepartmentsInUse()) return;

    const sourceName =
      getValues(
        `${sourcePath as NetworkNodeFieldPath}.name` as const,
      )?.trim() || 'ללא שם';

    setSelectedSourceName(sourceName);
    setPendingSourcePath(sourcePath as NetworkNodeFieldPath);
    setShowCopyConfirmation(true);
  };

  const handleCloseCopyConfirmation = () => {
    setShowCopyConfirmation(false);
    setPendingSourcePath(null);
    setSelectedSourceName(null);
  };

  const handleConfirmCopy = () => {
    if (!pendingSourcePath) return;
    if (blockReplaceIfDepartmentsInUse()) {
      handleCloseCopyConfirmation();
      return;
    }

    const sourceDepts =
      getValues(`${pendingSourcePath}.departments` as const) ?? [];
    const cloned = cloneDepartmentsFromSource(sourceDepts);

    replace(cloned);
    setValue(`${selectedFieldPath}.departments`, cloned, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue(`${selectedFieldPath}.roles`, []);
    setDefaultSelectedNodeForPath(selectedFieldPath, prev => ({
      ...prev,
      department: 'manual',
    }));
    setShowCopyConfirmation(false);
    setPendingSourcePath(null);
  };

  const subtitle =
    pendingDepartment === 'manual' ? (
      <p>
        מעבר <span className="font-bold">ידנית</span> יאפס את ההגדרות בתבנית
        שהוגדרו. האם להמשיך?
      </p>
    ) : (
      <p>
        מעבר <span className="font-bold">לתבנית</span> יאפס את ההגדרות האישיות
        שהוגדרו. האם להמשיך?
      </p>
    );

  const copySubtitle = (
    <p>
      העתקת מחלקות מ-
      <span className="font-bold">{selectedSourceName || 'ללא שם'}</span> תחליף
      את המחלקות הנוכחיות ותאפס את התפקידים. האם להמשיך?
    </p>
  );

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <p className="m-0 text-xl text-deepAqua font-bold">הגדרת מחלקות</p>
            <OnboardingValidationBadge show={showDepartmentValidation}  />
          </div>

          <p className="m-0 text-base text-darkerGray">
            הגדירו את המחלקות הקיימות בארגונכם
          </p>
        </div>
        <div>
          <Switcher
            active={defaultSelectedNode.department}
            onChange={handleSwitcherChange}
          />
        </div>
        <div className="flex justify-center md:justify-end">
          <AppMenu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onOpen={e => setMenuAnchorEl(e.currentTarget as HTMLElement)}
            onClose={() => setMenuAnchorEl(null)}
            items={menuItems}
            disableMenuIcon={menuItems.length === 0}
            onItemClick={handleSourceSelect}
            minWidth={260}
            menuIcon={
              <AppButton
                variant="primary"
                disabled={menuItems.length === 0}
                iconEnd={
                  <img
                    src={SVG.dropdownWhiteIcon}
                    alt="icon"
                    className="w-[12px] h-[6px] object-contain shrink-0"
                  />
                }>
                {buttonLabel}
              </AppButton>
            }
          />
        </div>
      </div>

      <AppConfirmationDialog
        open={showConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmChange}
        icon={
          <img src={SVG.infoBlueIcon} alt="" className="w-[48px] h-[48px]" />
        }
        title="שימו לב"
        subTitle={subtitle}
        confirmBtnText="כן"
        cancelBtnText="לא"
        cancelBtnTextClasses="!text-raspberryRed"
        bordered={false}
      />

      <AppConfirmationDialog
        open={showCopyConfirmation}
        onClose={handleCloseCopyConfirmation}
        onConfirm={handleConfirmCopy}
        icon={
          <img src={SVG.infoBlueIcon} alt="" className="w-[48px] h-[48px]" />
        }
        title="שימו לב"
        subTitle={copySubtitle}
        confirmBtnText="כן"
        cancelBtnText="לא"
        cancelBtnTextClasses="!text-raspberryRed"
        bordered={false}
      />
    </>
  );
}

export default DepartmentHeader;
